/**
 * modalManager.js
 * ---------------------------------------------
 * 弹窗调度管理器
 * 
 * 目的：
 * 统一管理多个弹窗的展示顺序，确保同一时间只展示一个弹窗。
 * 支持设置弹窗优先级，并自动排队展示，无需在各弹窗组件中手动判断逻辑。
 * 
 * 使用方式：
 * 1. 在业务逻辑中通过 modalManager.add('a') 添加待展示弹窗（可多次添加不同弹窗）；
 * 2. 弹窗通过 modalManager.state.visibleMap.xxx 控制展示；
 * 3. 弹窗关闭时需调用 modalManager.close('xxx') 通知调度器展示下一个；
 * 
 * 弹窗展示优先级（可在 modalPriority 中自定义）：
 * 例如：新手引导（c）优先于公告（a），公告优先于助力类弹窗（b, e, f, g）；
 * 
 * 示例代码：
 * 
 * // 需要展示弹窗时添加弹窗到队列中：
 * modalManager.add('c')
 * modalManager.add('a')
 * modalManager.add('b')
 * 
 * // computed中添加modalManager
 * computed: {
 *   modalManager() {
 *     return modalManager
 *   }
 * }
 *
 * // 弹窗组件绑定 visible：
 * <my-modal
 *   v-model="modalManager.state.visibleMap.a"
 *   @close="modalManager.close('a')"
 * />
 * 
 * 特性：
 * - 支持任意顺序添加弹窗，内部自动排序；
 * - 响应式可用于模板绑定；
 * - 避免多个弹窗同时展示；
 * 
 * ---------------------------------------------
 */

import Vue from 'vue'

// 目前优先级：新手引导 > 公告； 其他大于 > 新手引导

const modalPriority = {
  c: 1, // 新手引导
  a: 2, // 公告
  b: 3, // 为好友助力进行中
  d: 3, // 不能助力
  e: 3, // 被助力成功提醒
  f: 3, // 为好友助力成功
}

// 响应式状态对象
const state = Vue.observable({
  currentModal: null,
  queue: [],
  visibleMap: {
    c: false,
    a: false,
    b: false,
    d: false,
    e: false,
    f: false,
    g: false
  }
})

let scheduleTimer = null

const modalManager = {
  state,

  add(modalKey) {
    if (!modalPriority[modalKey]) return

    if (
      state.queue.includes(modalKey) ||
      state.currentModal === modalKey
    ) return

    state.queue.push(modalKey)
 
    // 延迟调度弹窗展示（去抖处理）
    if (!scheduleTimer) {
      scheduleTimer = setTimeout(() => {
        this._processQueue()
        scheduleTimer = null
      }, 0)
    }
  },

  close(modalKey) {
    if (state.currentModal === modalKey) {
      Vue.set(state.visibleMap, modalKey, false)
      state.currentModal = null
      this._processQueue()
    }
  },

  _processQueue() {
    if (state.currentModal || state.queue.length === 0) return

    // 根据优先级排序
    state.queue.sort((a, b) => modalPriority[a] - modalPriority[b])

    const next = state.queue.shift()
    state.currentModal = next
    Vue.set(state.visibleMap, next, true)
  }
}

export default modalManager
